import React, { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import api from "../../services/axiosInstance";
import ReportPreviewModal from "../common/ReportPreviewModal";
import { buildMRPDF, downloadMRPDF } from "../../utils/mrPdfUtils";

/**
 * Material Request preview / print modal.
 * Fetches MR details, generates a branded PDF via jsPDF (matching the GRN/PR
 * report style), then shows it in the shared ReportPreviewModal with
 * preview + download actions.
 *
 * Usage:
 *   <MRPreviewModal mrId={id} visible={visible} onCancel={...} />
 */
const MRPreviewModal = ({ mrId, visible, onCancel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfDataUri, setPdfDataUri] = useState(null);

  const fetchMRData = useCallback(async () => {
    setLoading(true);
    setPdfDataUri(null);
    try {
      const res = await api.get(`/inventory/material_request/get.php?mr_id=${mrId}`);
      if (res.data && res.data.status === "success" && res.data.dataset) {
        setData(res.data.dataset);
        try {
          const doc = await buildMRPDF(res.data.dataset);
          setPdfDataUri(doc.output("bloburl"));
        } catch (pdfError) {
          console.error("PDF generation failed:", pdfError);
          message.error("Failed to generate PDF preview.");
        }
      } else {
        message.error("Failed to load Material Request details.");
      }
    } catch (error) {
      message.error("Error loading Material Request.");
    } finally {
      setLoading(false);
    }
  }, [mrId]);

  useEffect(() => {
    if (visible && mrId) {
      fetchMRData();
    } else if (!visible) {
      setData(null);
      setPdfDataUri(null);
    }
  }, [visible, mrId, fetchMRData]);

  const handleDownload = async () => {
    try {
      if (!data) return;
      await downloadMRPDF(data);
      message.success("PDF downloaded successfully.");
    } catch (error) {
      console.error("PDF download failed:", error);
      message.error("Failed to download PDF.");
    }
  };

  return (
    <ReportPreviewModal
      visible={visible}
      onCancel={onCancel}
      title={`Material Request - ${data?.mr_ref || ""}`}
      pdfDataUri={pdfDataUri}
      loading={loading}
      showFiltersOption={false}
      onDownload={handleDownload}
    />
  );
};

export default MRPreviewModal;
